import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Button } from '@/components/ui/button';
import { MessageCircle, ThumbsUp, Share2 } from 'lucide-react';
import useTranslation from '@/hooks/use-translation';
import { useMode } from '@/contexts/ModeContext';

const SAMPLE_POSTS = [
  {
    id: 1,
    author: 'Sarah Chen',
    avatar: '/avatars/sarah.jpg',
    title: 'Water-Saving Success Story',
    content: 'Reduced my household water usage by 30% using AquaGenius recommendations!',
    likes: 24,
    comments: 8,
    shares: 3
  },
  {
    id: 2,
    author: 'Michael Rodriguez',
    avatar: '/avatars/michael.jpg',
    title: 'Smart Irrigation Tips',
    content: 'Here is how I optimized my farm irrigation using weather predictions...',
    likes: 42,
    comments: 15,
    shares: 7
  }
];

const Community = () => {
  const { translate } = useTranslation();
  const { mode } = useMode();

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold tracking-tight">
          {translate("Community Hub")}
        </h1>
        <p className="text-muted-foreground">
          {translate(
            mode === 'household'
              ? "Connect with other water-conscious households"
              : "Share agricultural water management experiences"
          )}
        </p>
      </div>

      <div className="grid gap-4">
        {SAMPLE_POSTS.map((post) => (
          <Card key={post.id} className="overflow-hidden hover:shadow-lg transition-shadow">
            <CardHeader>
              <div className="flex items-center gap-4">
                <Avatar>
                  <AvatarImage src={post.avatar} />
                  <AvatarFallback>{post.author[0]}</AvatarFallback>
                </Avatar>
                <div>
                  <CardTitle className="text-lg">{translate(post.title)}</CardTitle>
                  <p className="text-sm text-muted-foreground">{post.author}</p>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              <p className="mb-4">{translate(post.content)}</p>
              <div className="flex gap-4">
                <Button 
                  variant="ghost" 
                  size="sm"
                  className="hover:bg-primary/10 hover:text-primary transition-colors"
                >
                  <ThumbsUp className="mr-2 h-4 w-4" />
                  {post.likes}
                </Button>
                <Button 
                  variant="ghost" 
                  size="sm"
                  className="hover:bg-primary/10 hover:text-primary transition-colors"
                >
                  <MessageCircle className="mr-2 h-4 w-4" />
                  {post.comments}
                </Button>
                <Button 
                  variant="ghost" 
                  size="sm"
                  className="hover:bg-primary/10 hover:text-primary transition-colors"
                >
                  <Share2 className="mr-2 h-4 w-4" />
                  {post.shares}
                </Button>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      <Card className="overflow-hidden hover:shadow-lg transition-shadow">
        <CardHeader>
          <CardTitle>{translate("Join the Discussion")}</CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-muted-foreground mb-4">
            {translate(
              mode === 'household'
                ? "Share your water conservation tips and experiences with the community"
                : "Share your agricultural water management practices and learn from others"
            )}
          </p>
          <Button 
            className="w-full sm:w-auto bg-primary hover:bg-primary/90 text-white"
          >
            {translate("Create Post")}
          </Button>
        </CardContent>
      </Card>
    </div>
  );
};

export default Community; 